import type { AppEvents, EventBus } from "../contexts/events";
import { logger, LOGS } from "../utils";

type Subscription = [
    type: keyof AppEvents,
    handler: (event: AppEvents[keyof AppEvents]) => void,
];

/**
 * Bus d'une session SDK : relaie vers le bus de l'app tant que la session vit, puis `dispose()`
 * retire d'un coup tous les handlers posés par la session et rend inerte tout appel tardif.
 */
export class SessionEventBus implements EventBus {
    private readonly appEventBus: EventBus;

    private readonly subscriptions: Subscription[] = [];

    private disposed = false;

    constructor(appEventBus: EventBus) {
        this.appEventBus = appEventBus;
    }

    public emit: EventBus["emit"] = (type, event) => {
        if (this.disposed) {
            logger.warn(
                LOGS.Events,
                `[Event] ${type} ignoré : session SDK terminée`,
            );
            return;
        }
        this.appEventBus.emit(type, event);
    };

    public on: EventBus["on"] = (type, handler) => {
        if (this.disposed) return;
        this.subscriptions.push([type, handler as Subscription[1]]);
        this.appEventBus.on(type, handler);
    };

    public off: EventBus["off"] = (type, handler) => {
        if (!handler) {
            throw new Error(
                `SessionEventBus.off("${type}") sans handler retirerait aussi les abonnés de l'app`,
            );
        }
        const index = this.subscriptions.findIndex(
            ([subscribedType, subscribedHandler]) =>
                subscribedType === type && subscribedHandler === handler,
        );
        if (index >= 0) this.subscriptions.splice(index, 1);
        this.appEventBus.off(type, handler);
    };

    public dispose(): void {
        this.disposed = true;
        for (const [type, handler] of this.subscriptions.splice(0)) {
            this.appEventBus.off(type, handler);
        }
    }
}
